// Static module roster for the Settings area — host-owned (ADR-0014).
//
// Ported verbatim from the pbdesign wireframe `data/apps.ts` (the behaviour
// oracle). This is the FULL vision roster, shown statically for parity in the
// PlayerAI per-module, Workspace org-module, and Launcher favourites surfaces.
//
// It is deliberately NOT derived from `modules/launcher-entries.ts` /
// `ALL_MODULES`: `ModuleId` covers the mountable modules, while this roster
// also includes future concepts; `getEnabledModules()` returns only the
// modules enabled by runtime configuration and included in the tenant
// allowlist,
// so neither can represent the ten-app roster. Real module enablement is
// deployment configuration (config.js / K8s ConfigMap — see apps/shell/CLAUDE.md);
// the Settings toggles over this roster mutate mock state only.
//
// The COLOURS, however, are not this file's to invent: a mountable module's
// glyph and tile read the module identity ramp through the launcher
// presentation table (ADR-0092), so the two surfaces can never drift.

import {
  ChartNoAxesColumn,
  Lightbulb,
  Network,
  type LucideIcon,
} from "lucide-react";
import { KaizenBurst } from "@playbookai/ui";
import {
  MODULE_DOT_CLASS,
  moduleAccentTextClass,
  moduleIcon,
} from "../modules/launcher-entries.js";

export interface SettingsModule {
  readonly id: string;
  /** Canonical Auth module key when it differs from the presentation ID. */
  readonly authModuleKey?: string;
  /** Persisted launcher id when the legacy Settings id differs. */
  readonly launcherPreferenceId?: string;
  /** Runtime shell module id when it differs from the persisted id. */
  readonly launcherModuleId?: string;
  readonly name: string;
  /** One-line capability tag shown under the name. */
  readonly short: string;
  readonly icon: LucideIcon;
  /** Design-token text class for the glyph (e.g. `text-pb-gold-text`). */
  readonly accent: string;
  /** Design-token fill for the launcher favourite tile (the canvas' `.fav-ic`). */
  readonly tile: string;
}

export const SETTINGS_MODULES: readonly SettingsModule[] = [
  {
    id: "hoshin",
    name: "Hoshin",
    short: "Strategy deployment",
    icon: moduleIcon("hoshin"),
    accent: moduleAccentTextClass("hoshin"),
    tile: MODULE_DOT_CLASS.hoshin,
  },
  {
    id: "dpm",
    name: "DPM",
    short: "Daily performance",
    icon: moduleIcon("dpm"),
    accent: moduleAccentTextClass("dpm"),
    tile: MODULE_DOT_CLASS.dpm,
  },
  {
    id: "solve",
    name: "SOLVE",
    short: "Problem solving",
    icon: moduleIcon("solve"),
    accent: moduleAccentTextClass("solve"),
    tile: MODULE_DOT_CLASS.solve,
  },
  {
    id: "kaizen",
    name: "Kaizen Manager",
    short: "Continuous improvement",
    /* The burst that marks an improvement opportunity on a value stream map,
       not `Recycle` — a Mobius loop means recycling, not kaizen. */
    icon: KaizenBurst,
    accent: "text-pb-teal-text",
    tile: "bg-pb-teal",
  },
  {
    id: "vsm",
    name: "VSM",
    short: "Value stream mapping",
    icon: moduleIcon("vsm"),
    accent: moduleAccentTextClass("vsm"),
    tile: MODULE_DOT_CLASS.vsm,
  },
  {
    id: "kpi-dashboard",
    name: "KPI Dashboard & Strategy Room",
    short: "Performance · strategy · CI",
    icon: ChartNoAxesColumn,
    accent: "text-pb-gold-text",
    tile: "bg-pb-gold",
  },
  {
    id: "lama",
    name: "LAMA",
    short: "Lean assessment",
    icon: moduleIcon("lama"),
    accent: moduleAccentTextClass("lama"),
    tile: MODULE_DOT_CLASS.lama,
  },
  {
    id: "knowledge",
    authModuleKey: "knowledge_studio",
    launcherPreferenceId: "knowledge-studio",
    launcherModuleId: "knowledge_studio",
    name: "Knowledge Studio",
    short: "Knowledge workspace",
    icon: moduleIcon("knowledge_studio"),
    accent: moduleAccentTextClass("knowledge_studio"),
    tile: MODULE_DOT_CLASS.knowledge_studio,
  },
  {
    id: "brainstorm",
    name: "Brainstorm",
    short: "Idea generation",
    icon: Lightbulb,
    accent: "text-pb-gold-text",
    tile: "bg-pb-gold",
  },
  {
    id: "transformation-map",
    name: "Transformation Map",
    short: "Connected work",
    icon: Network,
    accent: "text-pb-purple-text",
    tile: "bg-pb-tint-6",
  },
];

export const SETTINGS_MODULE_BY_ID: Record<string, SettingsModule> =
  Object.fromEntries(
    SETTINGS_MODULES.flatMap((module) => [
      [module.id, module],
      ...(module.launcherPreferenceId
        ? ([[module.launcherPreferenceId, module]] as const)
        : []),
      ...(module.launcherModuleId
        ? ([[module.launcherModuleId, module]] as const)
        : []),
    ]),
  );

export function launcherPreferenceIdOf(module: SettingsModule): string {
  return module.launcherPreferenceId ?? module.id;
}

/** Canonicalize rows written by the legacy Settings roster for the editor. */
export function normalizeLauncherPreferenceId(id: string): string {
  const module = SETTINGS_MODULE_BY_ID[id];
  return module ? launcherPreferenceIdOf(module) : id;
}

/** Resolve persisted/legacy preference ids to the shell's runtime module id. */
export function normalizeLauncherModuleId(id: string): string {
  const module = SETTINGS_MODULE_BY_ID[id];
  return module?.launcherModuleId ?? module?.authModuleKey ?? module?.id ?? id;
}

/** Every module enabled — the seed shape for per-module enablement maps. */
export function allModulesEnabled(): Record<string, boolean> {
  return Object.fromEntries(SETTINGS_MODULES.map((m) => [m.id, true]));
}
